import { useEffect, useMemo, useState } from 'react';
import type { DealPLOverrides, QBExpense } from '../types';
import { subscribeDealPLOverrides } from '../utils/dealPLOverrides';
import {
  flattenApprovedDesignerInvoiceJobCash,
  qbExpenseDedupeSets,
  type DesignerInvoiceJobCash,
} from '../utils/designerInvoicePL';
import { subscribeApprovedDesignerInvoices, type DesignerInvoiceDoc } from '../utils/designerInvoiceService';
import { projectPLExpenseTouchesDeal } from '../utils/projectPL';
import { useQbExpensesProjectPL } from './useQbExpensesProjectPL';

export function useJobPurchasePlActualInputs(dealId: string | undefined): {
  expenses: Array<QBExpense & { id: string }>;
  overrides: DealPLOverrides | null;
  designerInvoiceJobCash: DesignerInvoiceJobCash[];
} {
  const allExpenses = useQbExpensesProjectPL();
  const [overrides, setOverrides] = useState<DealPLOverrides | null>(null);
  const [designerInvoices, setDesignerInvoices] = useState<DesignerInvoiceDoc[]>([]);

  useEffect(() => {
    if (!dealId) {
      setOverrides(null);
      return;
    }
    return subscribeDealPLOverrides(dealId, setOverrides);
  }, [dealId]);

  useEffect(() => {
    return subscribeApprovedDesignerInvoices(setDesignerInvoices);
  }, []);

  const expenses = useMemo(
    () => (dealId ? allExpenses.filter(e => projectPLExpenseTouchesDeal(e, dealId)) : []),
    [allExpenses, dealId],
  );

  const designerInvoiceJobCash = useMemo(
    () => flattenApprovedDesignerInvoiceJobCash(designerInvoices, qbExpenseDedupeSets(allExpenses)),
    [designerInvoices, allExpenses],
  );

  const designerCashForDeal = useMemo(
    () => (dealId ? designerInvoiceJobCash.filter(c => c.dealId === dealId) : []),
    [designerInvoiceJobCash, dealId],
  );

  return {
    expenses,
    overrides,
    designerInvoiceJobCash: designerCashForDeal,
  };
}
